import React from 'react';
import { Target, TrendingUp, Shield, BarChart3, ArrowRight, Zap } from 'lucide-react';
import descriptionImage from '../../assets/medium-shot-business-team-working_23-2149271692.jpg_nigaw2.jpg';

const BlockchainPage = () => {
  const services = [
    {
      icon: BarChart3,
      title: 'Market Analysis',
      description: 'We provide in-depth market analysis and insights into the blockchain-based assets space, keeping you updated on price movements, regulatory changes, and emerging trends that impact your investments.'
    },
    {
      icon: TrendingUp,
      title: 'Investment Strategy',
      description: 'Our team collaborates with you to develop a tailored investment strategy for blockchain-based assets. We consider your financial goals and risk tolerance to ensure a sustainable approach to growth.'
    },
    {
      icon: Target,
      title: 'Digital Asset Selection',
      description: 'Choosing the right blockchain-based assets is crucial for success. We offer comprehensive asset selection and analysis services to help you make informed decisions based on rigorous research.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/80 to-navy-800/60"></div>
        <div className="absolute inset-0">
          <img
            src="/images/man-8575459_640.jpg"
            alt="Blockchain-based Assets Investment"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold font-serif mb-4">Blockchain-based Assets</h1>
          <p className="text-xl md:text-2xl text-gray-200">
            Home / <span className="text-gold-400">Sectors</span>
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-6">
              Comprehensive <span className="text-gold-600">Blockchain-based Asset Services</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                At COREVEST LIMITED, we understand the dynamic nature of the blockchain-based assets market, which is characterized by rapid technological innovation and evolving digital ecosystems. Our services are meticulously designed to help you navigate this transformative landscape with confidence and clarity. With a deep commitment to staying ahead of technological developments and regulatory frameworks, we provide you with the insights and support necessary to make informed decisions.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Whether you're a seasoned investor or new to the blockchain space, our comprehensive approach ensures that you have the tools and knowledge to achieve your financial goals effectively through strategic blockchain asset investments.
              </p>
            </div>
            
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src={descriptionImage}
                  alt="Blockchain Strategy"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/50 to-transparent"></div>
              </div>
            </div>
          </div>

          {/* Services Section */}
          <div className="mb-20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <div key={index} className="group bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                    <div className="inline-flex p-4 bg-gradient-to-r from-gold-500 to-gold-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-navy-900 mb-4">
                      {service.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mb-20">
            <button className="flex items-center space-x-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors duration-300">
              <span>Prev</span>
            </button>
            <div className="flex space-x-2">
              <div className="w-3 h-3 bg-gold-500 rounded-full"></div>
              <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
            </div>
            <button className="flex items-center space-x-2 px-6 py-3 bg-gold-500 hover:bg-gold-600 text-white rounded-lg transition-colors duration-300">
              <span>Next</span>
            </button>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl md:text-4xl font-bold font-serif mb-6">
              Ready to Explore Blockchain-based Assets?
            </h3>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Join the blockchain revolution with expert guidance and strategic planning. 
              Navigate the digital asset market with confidence and unlock new opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://online.amdconsults.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-gold-500 hover:bg-gold-600 text-white font-bold rounded-full transition-colors duration-300"
              >
                Start Investing
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <button className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-navy-900 font-bold rounded-full transition-all duration-300">
                Learn More
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlockchainPage; 